import { isZealotSessionIssuer } from '@zealot/shared';
import { safeLocalStorage } from '@zealot/shared/browser';
import { useEffect } from 'react';
import { setMagicToken } from '@/utils/auth';
import logger from '@/utils/logger';

const TOKEN_PARAM_EXCLUDED_PATHS = ['/unsubscribe'];

/**
 * Decode JWT payload without validation (client-side extraction only)
 */
const decodeTokenPayload = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (error) {
    logger.warn('Failed to decode URL token payload', { context: 'useUrlTokenHandler' });
    return null;
  }
};

/**
 * Check if a valid Auth0 access token already exists in localStorage.
 * A stored magic token never blocks injection: the token in the link was minted for this visit.
 */
const hasValidStoredToken = () => {
  if (typeof window === 'undefined') return false;

  const accessToken = safeLocalStorage.getItem('access_token');
  if (accessToken) {
    const payload = decodeTokenPayload(accessToken);
    if (payload?.exp && Date.now() < payload.exp * 1000 - 10000) {
      return true;
    }
  }

  return false;
};

// why: a reference-actor token (iss zealot-references) also travels in ?token= but is not a session;
// why: storing it makes every SDK call 401 for 30 days. The issuer rule is shared with the API.
const isSessionToken = (payload) => isZealotSessionIssuer(payload?.iss);

/**
 * Check if a URL token appears to be valid (not expired)
 */
const isTokenValid = (payload) => {
  if (!payload?.exp) return false;

  // Add 10 second buffer
  return Date.now() < payload.exp * 1000 - 10000;
};

/**
 * Remove token parameter from URL without page reload
 */
const stripTokenFromUrl = () => {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  if (!url.searchParams.has('token')) return;

  url.searchParams.delete('token');
  window.history.replaceState({}, '', url.toString());
};

/**
 * Root-level hook for handling URL tokens.
 *
 * This hook should be called once at the app root level (_app.jsx).
 * It ensures that:
 * 1. Tokens are stripped from the URL (security), except on paths in
 *    TOKEN_PARAM_EXCLUDED_PATHS that read the token themselves (e.g. /unsubscribe)
 * 2. URL tokens are injected into the store only if no valid token exists
 *
 * This provides consistent token handling across all pages without
 * requiring each page to implement its own token extraction logic.
 */
export const useUrlTokenHandler = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (TOKEN_PARAM_EXCLUDED_PATHS.includes(window.location.pathname)) return;

    // Guard against double-execution in React strict mode
    if (window.__URL_TOKEN_HANDLED__) return;
    window.__URL_TOKEN_HANDLED__ = true;

    const url = new URL(window.location.href);
    const urlToken = url.searchParams.get('token');

    // Always strip token from URL first (security - don't leave tokens in browser history)
    if (urlToken) {
      stripTokenFromUrl();
    }

    // If no URL token, nothing more to do
    if (!urlToken) return;

    const payload = decodeTokenPayload(urlToken);

    // If URL token isn't valid, don't inject it
    if (!isTokenValid(payload)) {
      logger.debug('URL token expired or invalid, not injecting');
      return;
    }

    if (!isSessionToken(payload)) {
      logger.debug('URL token is not a session token, not injecting');
      return;
    }

    // Only inject if no valid token already exists in store
    if (hasValidStoredToken()) {
      logger.debug('Valid token already exists in store, skipping injection');
      return;
    }

    // Inject the URL token into localStorage
    logger.info('Injecting URL token into store');
    try {
      setMagicToken(urlToken);
    } catch (_e) {
      logger.warn('Failed to persist URL token', { context: 'useUrlTokenHandler' });
    }
  }, []);
};
